import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      // Redirect to home page
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // ============ TTS Generation ============
  generateTTS: async (textData) => {
    try {
      const response = await api.post('/api/v1/tts/generate', textData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Generate TTS asynchronously (returns job_id immediately for polling)
  generateTTSWithUser: async (textData) => {
    try {
      const response = await api.post('/api/v1/tts/generate-with-api', textData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get job status for async TTS generation
  getJobStatus: async (jobId) => {
    try {
      const response = await api.get(`/api/v1/tts/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ============ Voices ============
  getVoices: async () => {
    try {
      const response = await api.get('/api/v1/voices');
      return response.data.voices;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getPublicVoices: async () => {
    try {
      const response = await api.get('/api/v1/voices/public');
      return response.data.voices;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getCustomVoices: async () => {
    try {
      const response = await api.get('/api/v1/voices/custom');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  uploadVoice: async (file, voiceName) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('voice_name', voiceName);

      const response = await api.post('/api/v1/voices/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteVoice: async (voiceId) => {
    try {
      const response = await api.delete(`/api/v1/voices/${voiceId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ============ History ============
  getUserHistory: async (limit = 50) => {
    try {
      const response = await api.get(`/api/v1/tts/history?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteHistoryItem: async (historyId) => {
    try {
      const response = await api.delete(`/api/v1/tts/history/${historyId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ============ Health & Auth ============
  healthCheck: async () => {
    try {
      const response = await api.get('/api/v1/health');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  googleSignIn: async (idToken) => {
    try {
      const response = await api.post('/api/v1/auth/google', {
        id_token: idToken,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getCurrentUser: async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No auth token found');
      }

      const response = await api.get('/api/v1/auth/me');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getUserCredits: async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No auth token found');
      }

      const response = await api.get('/api/v1/user/credits');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ============ API Keys ============
  createAPIKey: async (keyData) => {
    try {
      const response = await api.post('/api/v1/api-keys', keyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getUserAPIKeys: async () => {
    try {
      const response = await api.get('/api/v1/api-keys');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteAPIKey: async (keyId) => {
    try {
      const response = await api.delete(`/api/v1/api-keys/${keyId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  revokeAPIKey: async (keyId) => {
    try {
      const response = await api.post(`/api/v1/api-keys/${keyId}/revoke`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  signOut: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
  },

  // Get auth token
  getAuthToken: () => {
    return localStorage.getItem('authToken');
  },

  // Get user from localStorage
  getUserFromStorage: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // ============ Built-in Voices (Admin) ============
  uploadAdminVoice: async (file, voiceName, tags) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('voice_name', voiceName);
      formData.append('tags', tags);

      const response = await api.post('/api/v1/admin/voices/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAdminVoices: async () => {
    try {
      const response = await api.get('/api/v1/admin/voices');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteAdminVoice: async (voiceId) => {
    try {
      const response = await api.delete(`/api/v1/admin/voices/${voiceId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get public built-in voices (for all users) - uses existing /voices endpoint
  getPublicBuiltinVoices: async () => {
    try {
      const response = await api.get('/api/v1/voices');
      // Transform response to match expected format
      const voices = response.data.voices || [];
      return {
        status: 'success',
        voices: voices.map(v => typeof v === 'string' ? { name: v, isBuiltIn: true } : { ...v, isBuiltIn: true }),
        count: voices.length
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default apiService;
